import { Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Helpers } from "@core/utils/helpers";
import { ApplicationService } from "@core/data/application.service";
import { ApplicationConfigService } from "@core/data/application-config.service";
import { ApplicationPreviewItem } from "@core/interfaces/api-responses/application-preview-item";
import { AppConstants } from "@core/constants/app";
import dayjs from "dayjs";
import { take } from "rxjs/operators";
import { ApplicationProgressResponse } from "src/app/@core/interfaces/api-responses";
import { LanguageSelectionModalService } from "@core/services/language-selection-modal.service";

const APPLICATION_LIST_PAGE_SIZE = 10;

const DASHBOARD_LANG_BACK_TRAP_STATE = { dashboardLanguageBackTrap: true } as const;

@Component({
    selector: "app-dashboard",
    templateUrl: "./dashboard.component.html",
    styleUrls: ["./dashboard.component.scss"],
    standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
    readonly translationPrefix = "pages.dashboard";
    readonly backButtonTranslation = "wizard.actions.back";
    applications: ApplicationPreviewItem[] = [];
    sortDateDirection: "asc" | "desc" = "desc";
    loading = true;
    readonly skeletonRows = [0, 1, 2, 3, 4];
    readonly pageSize = APPLICATION_LIST_PAGE_SIZE;

    currentPage = 1;
    totalCount = 0;

    private readonly onWindowPopState = (): void => {
        this.ngZone.run(() => {
            this.languageSelectionModal.openChangeLanguage();
            history.pushState(DASHBOARD_LANG_BACK_TRAP_STATE, "", window.location.href);
        });
    };

    constructor(
        private router: Router,
        private applicationService: ApplicationService,
        private applicationConfigService: ApplicationConfigService,
        private languageSelectionModal: LanguageSelectionModalService,
        private ngZone: NgZone,
    ) {
        const skipLanguageModal = this.router.getCurrentNavigation()?.extras?.state?.["skipLanguageModal"] === true;
        if (!skipLanguageModal) {
            this.languageSelectionModal.openOnDashboardLoad();
        }
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    }

    get canGoPrevious(): boolean {
        return this.currentPage > 1;
    }

    get canGoNext(): boolean {
        return this.currentPage < this.totalPages;
    }

    startNewApplication() {
        this.router.navigate([AppConstants.ELIGIBILITY_CHECK_PAGE]);
    }

    openLanguageSelection(): void {
        this.languageSelectionModal.openChangeLanguage();
    }

    ngOnInit() {
        history.pushState(DASHBOARD_LANG_BACK_TRAP_STATE, "", window.location.href);
        window.addEventListener("popstate", this.onWindowPopState);
        this.loadApplications();
    }

    ngOnDestroy() {
        window.removeEventListener("popstate", this.onWindowPopState);
    }

    private loadApplications(): void {
        this.loading = true;
        const ordering = this.sortDateDirection === "desc" ? "-created_at" : "created_at";
        const listParams = Helpers.prepareGetParamsFromObject({
            page: String(this.currentPage),
            ordering,
        });
        this.applicationService.getApplicationList(listParams).subscribe({
            next: (res) => {
                this.applications = res.results;
                this.totalCount = res.count;
                this.loading = false;
            },
            error: () => {
                this.loading = false;
            },
        });
    }

    private isApplicationSubmitted(item: ApplicationPreviewItem): boolean {
        return item.submittedAt != null && String(item.submittedAt).trim() !== "";
    }

    displayStatus(item: ApplicationPreviewItem): string {
        if (this.isApplicationSubmitted(item)) {
            return this.translationPrefix + ".table.submitted";
        }
        return this.translationPrefix + ".table.pending";
    }

    formatRequestedEffDate(dateStr: string | null): string {
        if (!dateStr) return "—";
        const d = dayjs(dateStr);
        return d.isValid() ? d.format("M/D/YY") : "—";
    }

    getDisplayEffectiveDate(item: ApplicationPreviewItem): string | null {
        return item.coverageEffectiveDate ?? item.requestedEffectiveDate;
    }

    formatDateCreatedSubmitted(item: ApplicationPreviewItem): string {
        const dateStr = item.submittedAt || item.createdAt;
        if (!dateStr) return "—";
        const d = dayjs(dateStr);
        return d.isValid() ? d.format("M/D/YYYY, h:mm:ss A") : "—";
    }

    displayApplicationType(type: string): string {
        if (type === AppConstants.APP_TYPE_CHANGE) {
            return this.translationPrefix + ".table.changeEnrollment";
        }
        return this.translationPrefix + ".table.newEnrollment";
    }

    toggleDateSort(): void {
        if (this.loading) {
            return;
        }
        this.sortDateDirection = this.sortDateDirection === "desc" ? "asc" : "desc";
        this.currentPage = 1;
        this.loadApplications();
    }

    goToPreviousPage(): void {
        if (!this.canGoPrevious || this.loading) {
            return;
        }
        this.currentPage -= 1;
        this.loadApplications();
    }

    goToNextPage(): void {
        if (!this.canGoNext || this.loading) {
            return;
        }
        this.currentPage += 1;
        this.loadApplications();
    }

    getActionLabel(item: ApplicationPreviewItem): string {
        if (this.isApplicationSubmitted(item)) {
            return this.translationPrefix + ".table.view";
        }
        return this.translationPrefix + ".table.review";
    }

    reviewApplication(item: ApplicationPreviewItem) {
        if (this.isApplicationSubmitted(item)) {
            this.applicationConfigService.setConfig(item.id, item.requestedEffectiveDate);
            this.router.navigate([AppConstants.CONFIRMATION_PAGE, item.id]);
            return;
        }
        this.applicationService
            .getProgress(item.id)
            .pipe(take(1))
            .subscribe((response: ApplicationProgressResponse) => {
                const revision = typeof response.revision === "string" ? response.revision : response.revision?.revision;
                this.applicationConfigService.setConfig(item.id, item.requestedEffectiveDate, revision ?? null);
                this.router.navigate([AppConstants.INTRO_PAGE], {
                    state: {
                        requestedEffectiveDate: item.requestedEffectiveDate,
                        application: response,
                    },
                });
            });
    }
}
